import React, { useState, useRef, useEffect } from 'react';
import { Mic, MicOff, Loader2, Volume2, VolumeX } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Account, Transaction } from '../types';
import { getInstitutionById } from '../constants/institutions';
import { aiWsUrl } from '../lib/aiApi';

interface VoiceInputProps {
  onTranscription: (text: string) => void;
  isOnline: boolean;
  accounts: Account[];
  recentTransactions: Transaction[];
}

export const VoiceInput: React.FC<VoiceInputProps> = ({ onTranscription, isOnline, accounts, recentTransactions }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const audioContextRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const audioQueueRef = useRef<Int16Array[]>([]);
  const isPlayingRef = useRef(false);

  const stopRecording = () => {
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsRecording(false);
    setIsConnecting(false);
  };

  const startRecording = async () => {
    if (!isOnline) return;
    
    setIsConnecting(true);
    setError(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 16000 });
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;

      // Connect to the AI backend WebSocket (VPS via Caddy, o relativo en dev)
      const ws = new WebSocket(aiWsUrl('/api/voice'));
      wsRef.current = ws;

      ws.onopen = () => {
        // Send initial context to the server
        ws.send(JSON.stringify({
          type: 'start',
          payload: {
            accounts: accounts.map(a => {
              const instName = a.institutionId ? getInstitutionById(a.institutionId)?.name : '';
              return instName ? `${a.name} (${a.type} - ${instName})` : `${a.name} (${a.type})`;
            }),
            recentTransactions: recentTransactions.map(t => `${t.description}: ${t.type}`)
          }
        }));
        
        setIsConnecting(false);
        setIsRecording(true);
        source.connect(processor);
        processor.connect(audioContext.destination);
      };

      ws.onmessage = (event) => {
        const message = JSON.parse(event.data);

        if (message.type === 'tool_call') {
          const functionCalls = message.functionCalls;
          if (functionCalls && functionCalls.length > 0) {
            const responses = functionCalls.map((call: any) => {
              if (call.name === "registrarTransaccion") {
                const args = call.args;
                if (args && args.textoFinal) {
                  onTranscription(args.textoFinal);
                }
              }
              return {
                id: call.id,
                name: call.name,
                response: { result: "Éxito. Transacción añadida a la pantalla del usuario." }
              };
            });
            
            ws.send(JSON.stringify({
              type: 'tool_response',
              functionResponses: responses
            }));
          }
        } else if (message.type === 'audio' && !isMuted) {
          const base64Audio = message.data;
          const binary = atob(base64Audio);
          const bytes = new Int16Array(binary.length / 2);
          for (let i = 0; i < bytes.length; i++) {
            bytes[i] = (binary.charCodeAt(i * 2) & 0xFF) | (binary.charCodeAt(i * 2 + 1) << 8);
          }
          audioQueueRef.current.push(bytes);
          playNextInQueue();
        } else if (message.type === 'interrupted') {
          audioQueueRef.current = [];
          isPlayingRef.current = false;
        } else if (message.type === 'error') {
          console.error("Server Error:", message.message);
          setError(message.message || "Error de conexión con el servicio de voz.");
          stopRecording();
        } else if (message.type === 'closed') {
          stopRecording();
        }
      };

      ws.onerror = (err) => {
        console.error("WebSocket Error:", err);
        setError("Error de conexión con el servidor.");
        stopRecording();
      };

      ws.onclose = () => {
        stopRecording();
      };

      processor.onaudioprocess = (e) => {
        const inputData = e.inputBuffer.getChannelData(0);
        // Convert Float32 to Int16 PCM
        const pcmData = new Int16Array(inputData.length);
        for (let i = 0; i < inputData.length; i++) {
          pcmData[i] = Math.max(-1, Math.min(1, inputData[i])) * 0x7FFF;
        }
        
        // Convert to Base64
        const buffer = pcmData.buffer;
        const binary = String.fromCharCode(...new Uint8Array(buffer));
        const base64Data = btoa(binary);

        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({
            type: 'audio',
            data: base64Data
          }));
        }
      };

    } catch (err) {
      console.error("Microphone Error:", err);
      setError("No se pudo acceder al micrófono.");
      setIsConnecting(false);
    }
  };

  const playNextInQueue = async () => {
    if (isPlayingRef.current || audioQueueRef.current.length === 0 || !audioContextRef.current) return;

    isPlayingRef.current = true;
    const pcmData = audioQueueRef.current.shift()!;
    
    // Gemini Live API returns audio at 24000Hz
    const audioBuffer = audioContextRef.current.createBuffer(1, pcmData.length, 24000);
    const channelData = audioBuffer.getChannelData(0);
    for (let i = 0; i < pcmData.length; i++) {
      channelData[i] = pcmData[i] / 0x7FFF;
    }

    const source = audioContextRef.current.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(audioContextRef.current.destination);
    source.onended = () => {
      isPlayingRef.current = false;
      playNextInQueue();
    };
    source.start();
  };

  useEffect(() => {
    return () => stopRecording();
  }, []);

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={isRecording ? stopRecording : startRecording}
        disabled={isConnecting || !isOnline}
        className={`p-2 rounded-lg transition-all flex items-center gap-2 text-xs font-medium border ${
          isRecording 
            ? 'bg-red-500/20 border-red-500/30 text-red-400 animate-pulse' 
            : 'bg-zinc-800 border-zinc-700 text-zinc-300 hover:bg-zinc-700'
        } disabled:opacity-50`}
        title={isRecording ? "Detener voz" : "Hablar con Gemini"}
      >
        {isConnecting ? (
          <Loader2 className="animate-spin" size={16} />
        ) : isRecording ? (
          <MicOff size={16} />
        ) : (
          <Mic size={16} />
        )}
        <span className="hidden sm:inline">
          {isConnecting ? "Conectando..." : isRecording ? "Escuchando..." : "Usar Voz"}
        </span>
      </button>

      {isRecording && (
        <button
          type="button"
          onClick={() => setIsMuted(!isMuted)}
          className="p-2 bg-zinc-800 border border-zinc-700 text-zinc-400 rounded-lg hover:bg-zinc-700 transition-colors"
        >
          {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>
      )}

      <AnimatePresence>
        {error && (
          <motion.span
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0 }}
            className="text-[10px] text-red-400 font-medium"
          >
            {error}
          </motion.span>
        )}
      </AnimatePresence>
    </div>
  );
};
